// ** react imports
import { useState, useMemo, useEffect } from 'react';

// ** api methods for quiz template management
import {
  useUploadTemplateMutation,
  useCreateTemplateMutation,
  useGetActiveTemplateQuery,
  useGetAllTemplatesQuery,
  useGetTemplateByIdQuery,
  useUpdateTemplateByIdMutation,
  useActivateTemplateMutation,
  useDeleteTemplateByIdMutation,
  useRestoreTemplateMutation,
  useGetSectionsWithQuestionsQuery,
} from '../../../redux/slices/quizTemplate/slice';

export const useUploadTemplate = () => {
  try {
    // api call mutation
    const [
      uploadTemplate,
      {
        isLoading: isUploadTemplateLoading,
        isError: isUploadTemplateError,
        error: uploadTemplateError,
      },
    ] = useUploadTemplateMutation();

    // handlers
    const handleUploadTemplate = async data => {
      return await uploadTemplate(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUploadTemplateLoading,
        isUploadTemplateError,
        uploadTemplateError,
        uploadTemplate: handleUploadTemplate,
      }),
      [
        isUploadTemplateLoading,
        isUploadTemplateError,
        uploadTemplateError,
        handleUploadTemplate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error uploading template:', error);
    throw error;
  }
};

export const useCreateTemplate = () => {
  try {
    // api call mutation
    const [
      createTemplate,
      {
        isLoading: isCreateTemplateLoading,
        isError: isCreateTemplateError,
        error: createTemplateError,
      },
    ] = useCreateTemplateMutation();

    // handlers
    const handleCreateTemplate = async data => {
      return await createTemplate(data);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isCreateTemplateLoading,
        isCreateTemplateError,
        createTemplateError,
        createTemplate: handleCreateTemplate,
      }),
      [
        isCreateTemplateLoading,
        isCreateTemplateError,
        createTemplateError,
        handleCreateTemplate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error creating template:', error);
    throw error;
  }
};

export const useGetActiveTemplate = () => {
  try {
    // states
    const [data, setData] = useState({});

    // api call query
    const {
      data: getActiveTemplate,
      isLoading: isGetActiveTemplateLoading,
      isError: isGetActiveTemplateError,
      error: getActiveTemplateError,
      refetch: refetchGetActiveTemplate,
    } = useGetActiveTemplateQuery();

    // effect
    useEffect(() => {
      if (getActiveTemplate !== undefined) {
        setData(getActiveTemplate.data);
      }
    }, [getActiveTemplate]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetActiveTemplateLoading,
        isGetActiveTemplateError,
        getActiveTemplateError,
        refetchGetActiveTemplate,
      }),
      [
        data,
        isGetActiveTemplateLoading,
        isGetActiveTemplateError,
        getActiveTemplateError,
        refetchGetActiveTemplate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching active template:', error);
    throw error;
  }
};

export const useGetAllTemplates = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [query, setQuery] = useState({});

    // api call query
    const {
      data: getAllTemplates,
      isLoading: isGetAllTemplatesLoading,
      isError: isGetAllTemplatesError,
      error: getAllTemplatesError,
      isFetching: isGetAllTemplatesFetching,
      refetch: refetchGetAllTemplates,
    } = useGetAllTemplatesQuery(query, {
      skip: skipApi,
    });

    // handlers
    const handleGetAllTemplates = async query => {
      setSkipApi(false);
      setQuery(query);
    };

    // effect
    useEffect(() => {
      if (getAllTemplates !== undefined) {
        // pagination implementation was pending.
        setData({
          data: getAllTemplates.data.data,
          total: getAllTemplates.data.total,
        });
      }
    }, [getAllTemplates]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetAllTemplatesLoading,
        isGetAllTemplatesError,
        getAllTemplatesError,
        isGetAllTemplatesFetching,
        refetchGetAllTemplates,
        getAllTemplates: handleGetAllTemplates,
      }),
      [
        data,
        isGetAllTemplatesLoading,
        isGetAllTemplatesError,
        getAllTemplatesError,
        isGetAllTemplatesFetching,
        refetchGetAllTemplates,
        handleGetAllTemplates,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching all templates:', error);
    throw error;
  }
};

export const useGetTemplateById = () => {
  try {
    // states
    const [data, setData] = useState({});
    const [skipApi, setSkipApi] = useState(true);
    const [templateId, setTemplateId] = useState('');

    // api call query
    const {
      data: getTemplateDetailsById,
      isLoading: isGetTemplateDetailsByIdLoading,
      isError: isGetTemplateDetailsByIdError,
      error: getTemplateDetailsByIdError,
      refetch: refetchGetTemplateDetailsById,
    } = useGetTemplateByIdQuery(templateId, { skip: skipApi });

    // handlers
    const handleGetTemplateDetailsById = async templateId => {
      setSkipApi(false);
      setTemplateId(templateId);
    };

    // effect
    useEffect(() => {
      if (getTemplateDetailsById !== undefined) {
        setData(getTemplateDetailsById.data);
      }
    }, [getTemplateDetailsById]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetTemplateDetailsByIdLoading,
        isGetTemplateDetailsByIdError,
        getTemplateDetailsByIdError,
        refetchGetTemplateDetailsById,
        getTemplateDetailsById: handleGetTemplateDetailsById,
      }),
      [
        data,
        isGetTemplateDetailsByIdLoading,
        isGetTemplateDetailsByIdError,
        getTemplateDetailsByIdError,
        refetchGetTemplateDetailsById,
        handleGetTemplateDetailsById,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error getting template details:', error);
    throw error;
  }
};

export const useUpdateTemplate = () => {
  try {
    // api call mutation
    const [
      updateTemplate,
      {
        isLoading: isUpdateTemplateLoading,
        isError: isUpdateTemplateError,
        error: updateTemplateError,
      },
    ] = useUpdateTemplateByIdMutation();

    // handlers
    const handleUpdateTemplate = async (templateId, data) => {
      return await updateTemplate({ templateId, data });
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isUpdateTemplateLoading,
        isUpdateTemplateError,
        updateTemplateError,
        updateTemplate: handleUpdateTemplate,
      }),
      [
        isUpdateTemplateLoading,
        isUpdateTemplateError,
        updateTemplateError,
        handleUpdateTemplate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

export const useActivateTemplate = () => {
  try {
    // api call mutation
    const [
      activateTemplate,
      {
        isLoading: isActivateTemplateLoading,
        isError: isActivateTemplateError,
        error: activateTemplateError,
      },
    ] = useActivateTemplateMutation();

    // handlers
    const handleActivateTemplate = async templateId => {
      return await activateTemplate(templateId);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isActivateTemplateLoading,
        isActivateTemplateError,
        activateTemplateError,
        activateTemplate: handleActivateTemplate,
      }),
      [
        isActivateTemplateLoading,
        isActivateTemplateError,
        activateTemplateError,
        handleActivateTemplate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error activating template:', error);
    throw error;
  }
};

export const useDeleteTemplate = () => {
  try {
    // api call mutation
    const [
      deleteTemplate,
      {
        isLoading: isDeleteTemplateLoading,
        isError: isDeleteTemplateError,
        error: deleteTemplateError,
      },
    ] = useDeleteTemplateByIdMutation();

    // handlers
    const handleDeleteTemplate = async templateId => {
      return await deleteTemplate(templateId);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isDeleteTemplateLoading,
        isDeleteTemplateError,
        deleteTemplateError,
        deleteTemplate: handleDeleteTemplate,
      }),
      [
        isDeleteTemplateLoading,
        isDeleteTemplateError,
        deleteTemplateError,
        handleDeleteTemplate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

export const useGetSectionsWithQuestions = () => {
  try {
    // states
    const [data, setData] = useState([]);
    const [templateId, setTemplateId] = useState('');
    const [skipApi, setSkipApi] = useState(true);

    // api call query
    const {
      data: getSectionsWithQuestions,
      isLoading: isGetSectionsWithQuestionsLoading,
      isError: isGetSectionsWithQuestionsError,
      error: getSectionsWithQuestionsError,
      refetch: refetchGetSectionsWithQuestions,
    } = useGetSectionsWithQuestionsQuery(
      { templateId },
      {
        skip: skipApi,
      }
    );

    // handlers
    const handleGetSectionsWithQuestions = async templateId => {
      setTemplateId(templateId);
      setSkipApi(false);
    };

    // effect
    useEffect(() => {
      if (getSectionsWithQuestions !== undefined) {
        setData(getSectionsWithQuestions.data ?? getSectionsWithQuestions);
      }
    }, [getSectionsWithQuestions]);

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        data,
        isGetSectionsWithQuestionsLoading,
        isGetSectionsWithQuestionsError,
        getSectionsWithQuestionsError,
        refetchGetSectionsWithQuestions,
        getSectionsWithQuestions: handleGetSectionsWithQuestions,
      }),
      [
        data,
        isGetSectionsWithQuestionsLoading,
        isGetSectionsWithQuestionsError,
        getSectionsWithQuestionsError,
        refetchGetSectionsWithQuestions,
        handleGetSectionsWithQuestions,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error fetching sections with questions:', error);
    throw error;
  }
};

export const useRestoreTemplate = () => {
  try {
    // api call mutation
    const [
      restoreTemplate,
      {
        isLoading: isRestoreTemplateLoading,
        isError: isRestoreTemplateError,
        error: restoreTemplateError,
      },
    ] = useRestoreTemplateMutation();

    // handlers
    const handleRestoreTemplate = async templateId => {
      return await restoreTemplate(templateId);
    };

    // memoized value to avoid unnecessary re-renders
    const memoizedValue = useMemo(
      () => ({
        isRestoreTemplateLoading,
        isRestoreTemplateError,
        restoreTemplateError,
        restoreTemplate: handleRestoreTemplate,
      }),
      [
        isRestoreTemplateLoading,
        isRestoreTemplateError,
        restoreTemplateError,
        handleRestoreTemplate,
      ]
    );

    return memoizedValue;
  } catch (error) {
    console.error('Error restoring template:', error);
    throw error;
  }
};
